import React, { useState, useEffect, useRef, ReactNode, useMemo } from "react";
import { Card, Typography, message, Button } from "antd";
import { WelfareAssistantProps } from "./types";
import MessageInput from "./MessageInput";
import MessageCard, { MessageProps } from "./MessageCard";
import WelcomeSection from "./WelcomeSection";
import FeatureOptions, { FeatureOption } from "./FeatureOptions";
import { useMemoizedFn } from "ahooks";
import {
  API_ENDPOINTS,
  apiRequest,
  getSessionIdFromStorage,
  saveSessionIdToStorage,
} from "../config/api";
import "./WelfareAssistant.css";
import CombinedMessageCard from "./CombinedMessageCard";
import {
  getImageUrlByScene,
  getImageUrlBySticky,
  getInputMessageByUrl,
  getUrlParameter,
} from "../utils/urlUtils";

const WelfareAssistant: React.FC<WelfareAssistantProps> = () => {
  const [inputValue, setInputValue] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [conversation, setConversation] = useState<MessageProps[]>([]);
  const [sessionId, setSessionIdState] = useState<string>("");
  // const [streamContent, setStreamContent] = useState<string>("");
  const abortControllerRef = useRef<AbortController | null>(null);
  const contentAreaRef = useRef<HTMLDivElement>(null);
  const initSessionCalledRef = useRef<boolean>(false);
  const [allChunkContent, setAllChunkContent] = useState<string>("");
  const [typingComplete, setTypingComplete] = useState<boolean>(false);
  const aiMessageIndexRef = useRef<number>(-1);
  const isProcessingRef = useRef<boolean>(false); // Strict mutex-like flag to prevent multiple submissions
  const conversationRef = useRef<HTMLDivElement>(null);

  const lastInterruptAtRef = useRef<string | null>(null);

  const isContactFormDisplayedRef = useRef<boolean>(false);
  const streamCompletedRef = useRef<boolean>(false);

  const msgPlaceholderRef = useRef<string>("你有什么问题可以咨询我…");

  const userMobileRef = useRef<string>("");

  const initializing = useRef<boolean>(true);

  // 检查sticky参数，如果为"1"则隐藏新话题按钮
  const stickyRef = useRef<string>(getImageUrlBySticky());

  const initMsgRef = useRef<string>(getInputMessageByUrl());

  const scrollToBottom = (delay: number = 100) => {
    if (delay > 0) {
      setTimeout(() => {
        if (contentAreaRef.current) {
          contentAreaRef.current.scrollTop =
            contentAreaRef.current.scrollHeight;
        }
      }, delay);
    } else {
      if (contentAreaRef.current) {
        contentAreaRef.current.scrollTop = contentAreaRef.current.scrollHeight;
      }
    }
  };

  // 重置中断点的辅助函数
  const resetInterruptAt = () => {
    lastInterruptAtRef.current = null;
  };

  useEffect(() => {
    // 使用 ref 避免在严格模式下重复调用初始化函数
    if (initSessionCalledRef.current) return;

    const initSession = async () => {
      try {
        initSessionCalledRef.current = true;
        initializing.current = true;
        setAllChunkContent("");
        // 调用新会话接口
        console.log("调用 NEW_SESSION 接口创建新会话...");
        const data = await apiRequest(API_ENDPOINTS.NEW_SESSION, {
          method: "POST",
        });

        if (data && data.session_id) {
          // 更新会话ID状态
          setSessionIdState(data.session_id);
          // 存储会话ID到 localStorage
          saveSessionIdToStorage(data.session_id);
          console.log("初始化新会话ID:", data.session_id);
          initializing.current = false;
          initSend();
        } else {
          message.error("初始化会话失败，请刷新页面重试");
        }
      } catch (error) {
        console.error("初始化会话失败:", error);
        message.error("初始化会话失败，请刷新页面重试");
      } finally {
        initializing.current = false;
      }
    };
    // 组件挂载时调用初始化函数
    initSession();
  }, []);

  useEffect(() => {
    // 滚动到底部 - 只在消息变化时执行一次，延迟确保DOM更新完成
    setTimeout(() => {
      scrollToBottom(0);
    }, 100);
  }, [conversation]);

  // 处理功能选项点击
  const handleFeatureClick = (option: FeatureOption) => {
    resetInterruptAt();
    // 使用ref检查是否正在处理消息，这比使用state更可靠
    if (isProcessingRef.current) {
      console.log("正在处理消息，忽略新的点击");
      message.info("正在思考中，请等待回复完成后再次提问");
      return;
    }

    console.log("选择功能:", option.title);
    // 移除featureOptions消息
    setConversation((prev) =>
      prev.filter((msg) => msg.contentType !== "featureOptions")
    );

    const text = option.title;
    handleSubmit(text); // 直接调用提交函数
  };

  const initSend = useMemoizedFn(() => {
    if (initMsgRef.current != null) {
      handleSubmit(initMsgRef.current!);
    }
  });

  // 处理消息提交
  const handleSubmit = async (text: string) => {
    // 严格检查处理状态，使用ref比state更可靠
    if (isProcessingRef.current) {
      message.info("正在思考中，请等待回复完成后再次提问");
      return;
    }

    if (!text.trim() || initializing.current) {
      return;
    }

    // 立即设置处理标志，防止并发提交
    isProcessingRef.current = true;
    setLoading(true);

    // 终止之前的请求（如果有）
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    console.log("发送消息 ", text);

    // 检查消息是否包含Markdown语法
    const containsMarkdown =
      /(\*\*|__|\*|_|#|`|```|\[|\]|\(|\)|>|---|---|<http)/.test(text);
    const contentType = containsMarkdown ? "markdown" : "text";

    // 添加用户消息到对话
    const userMessage: MessageProps = {
      type: "user",
      content: text,
      contentType: contentType,
    };

    setConversation((prev) => [...prev, userMessage]);

    setInputValue(""); // 清空输入框
    setAllChunkContent(""); // 重置累积内容
    streamCompletedRef.current = false; // Reset stream completed state
    try {
      const params = new URLSearchParams({
        content: text,
      });

      // 添加会话ID参数（如果有）
      if (sessionId) {
        params.append("session_id", sessionId);
      }

      // 如果有lastInterruptAt，添加到请求中
      if (lastInterruptAtRef.current) {
        params.append("interrupt_at", lastInterruptAtRef.current);
      }

      // params.append("sticky", stickyRef.current);

      userMobileRef.current = "";
      // 创建EventSource进行流式处理
      const streamUrl = `${API_ENDPOINTS.CHAT}?${params.toString()}`;

      await sendStreamMessage(text, streamUrl);
    } catch (error) {
      msgPlaceholderRef.current = "你有什么问题可以咨询我…";
      resetAllStatus();
      // 移除思考中的消息
      setConversation((prev) => prev.filter((msg) => !msg.isThinking));

      if (error instanceof Error && error.name !== "AbortError") {
        console.error("Error processing chat:", error);
        message.error("获取回复失败，请重试");
        // 添加错误消息
        setConversation((prev) => [
          ...prev,
          {
            type: "assistant",
            content: "抱歉，我暂时无法回答您的问题。请稍后再试。",
          } as MessageProps,
        ]);
      }
    }
  };

  // 发送流式消息
  const sendStreamMessage = async (userMessage: string, streamUrl: string) => {
    // 确保所有代码路径都会重置 isProcessingRef 标志
    try {
      // 但要确保重置其他状态
      streamCompletedRef.current = false; // Reset stream completed state at the start
      // 清除所有"思考中"消息，确保只有一个正在进行的消息
      setConversation((prev) => prev.filter((msg) => !msg.isThinking));
      // 添加新的思考中消息
      setConversation((prev) => [
        ...prev,
        {
          type: "assistant",
          content: "思考中...",
          isThinking: true,
        } as MessageProps,
      ]);

      msgPlaceholderRef.current = "正在回复中...";

      // 终止之前的EventSource连接（如果有）
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // 创建新的AbortController
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      // 创建EventSource对象
      const eventSource = new EventSource(streamUrl);
      eventSource.onmessage = (event) => {
        // 检查是否已经被中止
        if (abortController.signal.aborted) {
          eventSource.close();
          return;
        }

        try {
          let data = JSON.parse(event.data);
          if (data.type === "CLOSE") {
            // 保存中断
            eventSource.close();
            handleOnMessageClose(data);
          }
        } catch (error) {
          console.log("无法解析JSON", error);
        }
      };

      eventSource.addEventListener("interrupt", (event) => {
        // 检查是否已经被中止
        if (abortController.signal.aborted) {
          eventSource.close();
          return;
        }

        console.log("Received interrupt:", event.data);
        if (event.data.trim() === "END") {
          resetInterruptAt();
          resetAllStatus();
          return;
        }

        // 解析事件数据，如果有特定内容再决定是否显示表单或处理组合消息
        try {
          const data = JSON.parse(event.data);
          processInterruptEventData(data);
          // 替换思考中消息，而不是添加新消息
          setConversation((prev) => {
            const newConv = [...prev];
            // 找到思考中消息
            for (let i = newConv.length - 1; i >= 0; i--) {
              if (newConv[i].isThinking) {
                newConv[i] = {
                  type: "assistant",
                  content: data.data[0].content,
                  contentType: "markdown",
                  isThinking: false,
                };
                break;
              }
            }
            return newConv;
          });
        } catch (error) {
          console.log("无法解析JSON", error);
        }
        // 滚动到底部
        scrollToBottom();
        console.log("interrupt event listener: Calling resetInterruptAt");
      });

      eventSource.addEventListener("whole_messages", (event) => {
        // 检查是否已经被中止
        if (abortController.signal.aborted) {
          eventSource.close();
          return;
        }
        handleWholeMessages(event);
      });

      // chunk 事件监听器 - 负责解码数据和更新累积内容
      eventSource.addEventListener("chunk", (event) => {
        // 检查是否已经被中止
        if (abortController.signal.aborted) {
          eventSource.close();
          return;
        }

        if (event.data.trim() === "END") {
          console.log("chunk END");
          resetInterruptAt();
          resetAllStatus();
          setConversation((prev) => {
            // 过滤掉思考中和空消息
            return prev.filter((msg) => !msg.isThinking && msg.content !== "");
          });
          return;
        }

        try {
          // 解码数据
          const decodedData = event.data.replace(/__n__/g, "\n");
          // 使用函数式更新确保使用最新状态
          setAllChunkContent((prevContent) => {
            const newContent = prevContent + decodedData;
            // 直接在这里更新会话内容，替换思考中消息
            setConversation((prev) => {
              const newConv = [...prev];
              // 找到思考中消息
              let foundThinking = false;
              for (let i = newConv.length - 1; i >= 0; i--) {
                if (newConv[i].isThinking) {
                  newConv[i] = {
                    type: "assistant",
                    content: newContent,
                    contentType: "markdown",
                    isThinking: false,
                    isChunk: true,
                  };
                  foundThinking = true;
                  break;
                }
              }

              // 如果没有找到思考中消息，找最后一条AI消息
              if (!foundThinking) {
                for (let i = newConv.length - 1; i >= 0; i--) {
                  if (
                    newConv[i].type === "assistant" &&
                    !newConv[i].isThinking
                  ) {
                    newConv[i] = {
                      ...newConv[i],
                      content: newContent,
                      contentType: "markdown",
                      isChunk: true,
                    };
                    break;
                  }
                }
              }
              return newConv;
            });
            return newContent;
          });

          // 滚动到底部
          scrollToBottom();
        } catch (error) {
          console.error("Error processing chunk:", error);
          // 如果解码失败，使用函数式更新
          setAllChunkContent((prevContent) => prevContent + event.data);
        }
      });

      eventSource.onerror = () => {
        console.error("EventSource encountered an error");

        // 确保关闭EventSource连接
        eventSource.close();

        msgPlaceholderRef.current = "你有什么问题可以咨询我…";

        resetAllStatus();

        // 清除AbortController引用
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }

        // 移除思考中消息
        setConversation((prev) => prev.filter((msg) => !msg.isThinking));
        // 如果流内容为空，添加错误消息
        setConversation((prev) => [
          ...prev,
          {
            type: "assistant",
            content: "抱歉，我暂时无法回答您的问题。请稍后再试。",
            contentType: "text",
          } as MessageProps,
        ]);
      };
    } catch (error) {
      // 捕获任何未预期的错误，确保状态被重置
      console.error("Unexpected error in sendStreamMessage:", error);
      resetAllStatus();

      // 移除思考中消息
      setConversation((prev) => prev.filter((msg) => !msg.isThinking));

      // 添加错误消息
      setConversation((prev) => [
        ...prev,
        {
          type: "assistant",
          content: "抱歉，发生了错误。请重试。",
          contentType: "text",
        } as MessageProps,
      ]);

      scrollToBottom();
    }
  };

  const resetAllStatus = () => {
    streamCompletedRef.current = true; // Mark stream as completed
    setLoading(false); // 确保loading状态被重置
    isProcessingRef.current = false; // 确保重置处理标志
  };

  // Add the new function for processing interrupt event data
  const processInterruptEventData = (data: any) => {
    // 检查 data 是否是一个包含 data 数组的对象
    if (data && data.data && Array.isArray(data.data) && data.data.length > 0) {
      // 检查第一个元素是否有 type 字段，且值为 'form'
      if (data.data[0] && data.data[0].type === "form") {
        const flowTypeStr = data.data[0].flow_type || "福利方案";

        var flow_type_int = 1;

        if (flowTypeStr == "福利方案") {
          flow_type_int = 1;
        } else if (flowTypeStr == "福利产品") {
          flow_type_int = 2;
        } else if (flowTypeStr == "政策解读" || flowTypeStr == "福利政策") {
          flow_type_int = 3;
        }

        const contactMessage: MessageProps = {
          type: "assistant",
          content: "好的，我将继续为您生成更详细的福利方案",
          contentType: "contact",
          contactOptions: {
            onSubmit: handleContactSubmit,
            onSkip: handleContactSkip,
            flow_type: flow_type_int,
          },
        };

        isContactFormDisplayedRef.current = true;
        msgPlaceholderRef.current = "";

        setConversation((prev) => [...prev, contactMessage]);
        // 清除思考中消息
        setConversation((prev) => prev.filter((msg) => !msg.isThinking));
      }
      // 检查是否是文本+二维码组合消息
      else {
        // 收集不同类型的消息内容
        let markdownContent = "";
        let qrcodeContent = "";

        // 遍历消息数组，提取文本和二维码内容
        for (const item of data.data) {
          // 收集文本内容
          if (item.type === "text" || item.type === "markdown" || !item.type) {
            markdownContent += item.content || "";

            setConversation((prev) => {
              // 先过滤掉思考中的消息
              const filtered = prev.filter((msg) => !msg.isThinking);
              return [
                ...filtered,
                {
                  type: "assistant",
                  content: markdownContent,
                  contentType: "markdown",
                } as MessageProps,
              ];
            });
          }
          // 收集二维码内容
          else if (item.type === "qrcode") {
            qrcodeContent = getImageUrlByScene();
            const mobile = item.mobile;
            const flow_type = item.flow_type;

            if (flow_type == "福利方案") {
              if (mobile) {
                markdownContent =
                  "我已根据您的要求，生成了更详细的个性化福利方案，方案中的产品卡片均可直接点击查看产品介绍。另外您还可以点击上方图标下载本方案。友情提示，本方案是基于关爱通福利事务服务AI生成，仅供参考，不作为最终方案建议。如您需要更正式的福利方案咨询，可以添加下方联系方式。";
              } else {
                markdownContent = "好的，您也可以随时添加我们的专家进行咨询";
              }
            } else if (flow_type == "福利产品") {
              if (mobile) {
                markdownContent =
                  "好的，我们的专家将会尽快与您联系，您也可以随时添加我们的专家进行咨询。";
              } else {
                markdownContent = "好的，您也可以随时添加我们的专家进行咨询";
              }
            } else if (flow_type == "政策解读" || flow_type == "福利政策") {
              if (mobile) {
                markdownContent =
                  "好的，我们的专家将会尽快与您联系，您也可以随时添加我们的专家进行咨询。";
              } else {
                markdownContent = "好的，您也可以随时添加我们的专家进行咨询";
              }
            } else if (flow_type === "未分类") {
              markdownContent =
                "您的问题我暂时还无法解答，您可以随时添加我们的专家进行咨询。";
            }

            // 使用组合消息卡片组件
            const combinedCard = (
              <CombinedMessageCard
                markdownContent={markdownContent}
                qrcodeContent={qrcodeContent}
              />
            );

            // 更新对话内容
            setConversation((prev) => {
              // 先过滤掉思考中的消息
              const filtered = prev.filter((msg) => !msg.isThinking);
              return [
                ...filtered,
                {
                  type: "assistant",
                  content: combinedCard,
                  contentType: "combined",
                } as MessageProps,
                // 添加提示消息
                {
                  type: "assistant",
                  content: "您可以扫描上方二维码，或继续咨询其他问题。",
                  contentType: "text",
                } as MessageProps,
                // 添加功能选项作为消息
                {
                  type: "assistant",
                  content: "featureOptions",
                  contentType: "featureOptions",
                  featureOptions: {
                    onFeatureClick: handleFeatureClick,
                    loading: false,
                  },
                } as MessageProps,
              ];
            });
          }
        }
      }
    }
  };

  const handleWholeMessages = useMemoizedFn((event) => {
    console.log("Received whole_messages:", event.data);
    // 如果接受到的数据是 data: END 说明结束了
    if (event.data.trim() === "END") {
      console.log("whole_messages END");

      resetInterruptAt();

      console.log("userMobileRef.current", userMobileRef.current);

      if (userMobileRef.current) {
        //获取最后一条markdown消息
        let lastMarkdownMessageIndex = -1;
        for (let i = conversation.length - 1; i >= 0; i--) {
          if (
            conversation[i].type === "assistant" &&
            conversation[i].contentType === "markdown" &&
            conversation[i].isChunk
          ) {
            lastMarkdownMessageIndex = i;
            break;
          }
        }
        if (lastMarkdownMessageIndex !== -1) {
          setConversation((prev) => {
            const updated = [...prev];
            updated[lastMarkdownMessageIndex] = {
              ...updated[lastMarkdownMessageIndex],
              showCopyAndDownloadButtons: true,
            };
            console.log("updated", updated);
            return updated;
          });
        }
      }

      userMobileRef.current = "";
      resetAllStatus();
      scrollToBottom();
      return;
    }

    try {
      const data = JSON.parse(event.data);
      userMobileRef.current = data.data[0].mobile || "";
      processInterruptEventData(data);
    } catch (error) {
      console.error("Error processing whole_messages:", error);
    }
    resetAllStatus();
    scrollToBottom();
  });

  const handleFocus = () => {
    console.log("handleFocus");
    scrollToBottom(200);
  };

  // 处理新话题
  const handleNewTopic = (newSessionId: string) => {
    console.log("打开新话题，新会话ID:", newSessionId);

    // 如果正在处理消息，则终止当前请求
    if (isProcessingRef.current && abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    resetAllStatus();
    // 使用API返回的会话ID
    setSessionIdState(newSessionId);
    // 存储会话ID到 localStorage
    saveSessionIdToStorage(newSessionId);
    // 清空对话历史和中断点
    setConversation([]);
    // setStreamContent("");
    resetInterruptAt(); // 使用新的helper函数重置中断点
  };

  // Handle typing completion
  const handleTypingComplete = () => {
    setTypingComplete(true);
    console.log("Typing animation complete");
  };

  const submitUserData = async (data: {
    name: string;
    company: string;
    phone: string;
    flow_type: number;
  }) => {
    try {
      console.log("提交用户数据:", data);

      // 调用SUBMIT接口提交用户信息
      const response = await apiRequest(API_ENDPOINTS.SUBMIT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: data.name,
          enterprise_name: data.company,
          interrupt_at: lastInterruptAtRef.current,
          mobile: data.phone,
          flow_type: data.flow_type,
          scene: getUrlParameter("scene") ?? 1,
          session_id: sessionId, // 添加会话ID
        }),
      });

      if (response) {
        console.log("用户数据提交成功:", response);
        // 可以根据响应结果进行相应的处理
      }
    } catch (error) {
      console.error("提交用户数据失败:", error);
    }
  };

  const handleChatUserInfo = async (data: {
    name: string;
    company: string;
    phone: string;
  }) => {
    const formattedUserData = `${data.name}#${data.company}#${data.phone}`;

    const chatParams = new URLSearchParams({
      content: formattedUserData,
    });

    // 如果有lastInterruptAt，添加到请求中
    if (lastInterruptAtRef.current) {
      chatParams.append("interrupt_at", lastInterruptAtRef.current);
    }

    // 添加会话ID参数（如果有）
    const sessionId = getSessionIdFromStorage();
    if (sessionId) {
      chatParams.append("session_id", sessionId);
    }

    const streamUrl = `${API_ENDPOINTS.CHAT}?${chatParams.toString()}`;
    await sendStreamMessage("", streamUrl);
  };

  // Add this function to handle contact form submission
  const handleContactSubmit = async (data: {
    name: string;
    company: string;
    phone: string;
    flow_type: number;
  }) => {
    var text = "";
    text += `${data.name}`;
    if (text) {
      text += ",";
    }
    text += `${data.company}`;
    if (text) {
      text += ",";
    }
    text += `${data.phone}`;

    // 添加用户消息到对话
    const userMessage: MessageProps = {
      type: "user",
      content: text,
      contentType: "text",
    };

    if (data.flow_type == 1) {
      // 添加用户消息到对话
      const assistantsMessage: MessageProps = {
        type: "assistant",
        content: "好的，我将继续为您生成更详细的福利方案",
        contentType: "text",
      };
      setConversation((prev) => [...prev, userMessage, assistantsMessage]);
    } else {
      setConversation((prev) => [...prev, userMessage]);
    }

    isContactFormDisplayedRef.current = false;
    msgPlaceholderRef.current = "你有什么问题可以咨询我…";

    submitUserData(data);

    // 立即设置处理标志，防止并发提交
    isProcessingRef.current = true;
    setLoading(true);

    setInputValue(""); // 清空输入框
    setAllChunkContent(""); // 重置累积内容
    streamCompletedRef.current = false; // Reset stream completed state

    handleChatUserInfo(data);
  };

  // Add this function to handle skip button click
  const handleContactSkip = () => {
    resetAllStatus();
    isContactFormDisplayedRef.current = false;
    msgPlaceholderRef.current = "你有什么问题可以咨询我…";

    // 只移除最后一个contact消息
    setConversation((prev) => {
      const newConversation = [...prev];
      // 从后往前找最后一个contact消息
      for (let i = newConversation.length - 1; i >= 0; i--) {
        if (newConversation[i].contentType === "contact") {
          // 找到最后一个contact消息，删除它
          newConversation.splice(i, 1);
          break; // 只删除一个，然后退出循环
        }
      }
      return newConversation;
    });

    handleChatUserInfo({
      name: "",
      company: "",
      phone: "",
    });
  };

  const handleOnMessageClose = (data: any) => {
    console.log("Setting receivedWholeMessages to true from message CLOSE");

    resetAllStatus();

    // 立即更新状态，确保后续代码使用最新的数据
    lastInterruptAtRef.current = data.interrupt_at;

    // 清除所有"思考中"的消息
    setConversation((prev) => prev.filter((msg) => !msg.isThinking));
    msgPlaceholderRef.current = "你有什么问题可以咨询我…";
    if (isContactFormDisplayedRef.current) {
      msgPlaceholderRef.current = "";
    }
  };

  const finalConversation = useMemo(() => {
    console.log("conversation", conversation);
    return conversation.filter((msg) => msg.content !== "")
  }, [conversation])

  const renderMessages = () => {
    return (
      <>
        {conversation.length === 0 ? (
          // 当没有对话时，渲染欢迎组件和功能选项卡
          <>
            {stickyRef.current !== "1" && (
              <>
                <WelcomeSection />
                <FeatureOptions
                  onFeatureClick={handleFeatureClick}
                  loading={loading}
                />
              </>
            )}
          </>
        ) : (
          // 有对话时，显示消息列表
          <div className="messages-container" ref={conversationRef}>
            {finalConversation?.map((msg, index) => {
              return (
                <React.Fragment key={index}>
                  <MessageCard
                    type={msg.type}
                    content={msg.content}
                    contentType={msg.contentType}
                    isThinking={msg.isThinking}
                    streamCompleted={streamCompletedRef.current}
                    showCopyAndDownloadButtons={
                      msg.showCopyAndDownloadButtons
                    }
                    featureOptions={msg.featureOptions}
                    contactOptions={msg.contactOptions}
                  />
                </React.Fragment>
              );
            })}
          </div>
        )}
      </>
    );
  };

  // 组件卸载时清理所有状态
  useEffect(() => {
    return () => {
      // 中止所有正在进行的请求
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // 重置处理状态
      isProcessingRef.current = false;
    };
  }, []);

  // 添加这个函数来统一消息样式，确保宽度一致
  useEffect(() => {
    // 确保所有助手消息和联系人表单宽度一致
    const updateMessageStyles = () => {
      const messageContents = document.querySelectorAll(
        ".assistant-message .message-content"
      );
      messageContents.forEach((content) => {
        (content as HTMLElement).style.width = "100%";
        (content as HTMLElement).style.maxWidth = "100%";
      });
    };

    // 执行一次立即更新
    updateMessageStyles();

    // 在会话更新时重新执行
    const observer = new MutationObserver(updateMessageStyles);
    if (conversationRef.current) {
      observer.observe(conversationRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => observer.disconnect();
  }, [conversation]);

  return (
    <div
      className={`welfare-container ${stickyRef.current === "1" ? "sticky-welfare-container" : ""
        }`}
    >
      <Card className="welfare-card">
        <div className="content-area" ref={contentAreaRef}>
          {renderMessages()}
        </div>

        {/* 使用封装的MessageInput组件 */}
        <MessageInput
          value={inputValue}
          onChange={setInputValue}
          onSubmit={handleSubmit}
          onNewTopic={handleNewTopic}
          onFocus={handleFocus}
          loading={loading}
          placeholder={msgPlaceholderRef.current}
          isNewConversation={conversation.length === 0}
          hasConversation={conversation.length > 0}
          disabled={
            isProcessingRef.current ||
            (conversation.length > 0 && !streamCompletedRef.current) ||
            isContactFormDisplayedRef.current
          }
        />
      </Card>
    </div>
  );
};

export default WelfareAssistant;
